<template>
  <form action="#" method="post" class="slo-iot-roi-calculator">
    <div class="slo-iot-roi-calculator--form-input-group p-lg bg-gray-50 rounded-lg">
      <SloField class="justify-between">
        <SloLabel>{{ 'Your estimated hourly labor costs' | t }}</SloLabel>
        <SloInput v-model.number="hourly_labor_costs" name="labor_cost" type="number">
          <template v-slot:start>
            <SloIcon name="DollarSign" />
          </template>
        </SloInput>
      </SloField>
      <SloField>
        <SloLabel>{{ 'Your estimated hourly manager labor costs' | t }}</SloLabel>
        <SloInput v-model.number="hourly_manager_labor_costs" name="manager_cost" type="number">
          <template v-slot:start>
            <SloIcon name="DollarSign" />
          </template>
        </SloInput>
      </SloField>
      <SloField>
        <SloLabel>{{ 'Your facility total number of restrooms' | t }}</SloLabel>
        <SloInput v-model.number="total_restrooms" name="restroom_count" type="number" />
      </SloField>
      <SloField>
        <SloLabel>{{ 'Your estimated number of products' | t }}</SloLabel>
        <SloInput
          v-model.number="estimated_number_of_products"
          name="product_count"
          type="number"
        />
      </SloField>
    </div>
    <div class="mt-lg md:hide">
      <p>
        Scroll to view
        <span class="c-blue-400">
          <SloIcon name="ArrowRight" />
        </span>
      </p>
    </div>
    <div class="slo-iot-roi-calculator--form-input-calculator rounded-lg mt-lg border-1 border-gray-300">
      <div>
        <div class="bg-gray-50 border-b-1 border-gray-300 rounded-t-lg">
          <div class="px-md py-sm">
            <h3 class="text-xl fw-strong m-0">{{ 'Labor' | t }}</h3>
          </div>
          <div class="slo-iot-roi-calculator--section-row py-md">
            <div class="px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Savings and value points' | t }}</h4>
            </div>
            <div class="place-items-end px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Labor Hrs' | t }}</h4>
            </div>
            <div class="place-items-end px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Times per year' | t }}</h4>
            </div>
            <div class="place-items-end basis-1 px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Labor' | t }} $</h4>
            </div>
            <div class="place-items-end basis-1 px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Restrooms' | t }}</h4>
            </div>
            <div class="place-items-end basis-1 px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Savings' | t }}</h4>
            </div>
          </div>
        </div>
        <div class="slo-iot-roi-calculator--section-row items-center py-md border-b-1 border-gray-300">
          <div class="px-md">
            <p class="mb-sm c-gray-700 fw-bold">{{ 'Preventative maintenance' | t }}</p>
            <ul class="m-0">
              <li class="c-gray-700">{{ 'Labor hours saved as minimized repair time' | t }}</li>
              <li class="c-gray-700">{{ 'Determine hours of labor that could be saved' | t }}</li>
              <li class="c-gray-700">{{ 'Increased customer satisfaction' | t }}</li>
            </ul>
          </div>
          <div class="px-md">
            <SloField>
              <SloInput
                v-model.number="maintenance_labor_hours"
                name="maintenance_labor_hours"
                type="number"
                :disabled="!inputsEnabled"
              />
            </SloField>
          </div>
          <div class="px-md">
            <SloField>
              <SloSelect
                v-model.number="maintenance_labor_months"
                name="maintenance_labor_months"
                :disabled="!inputsEnabled"
              >
                <option value="" selected disabled></option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
              </SloSelect>
            </SloField>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p v-if="hourly_labor_costs" class="c-gray-700">${{ hourly_labor_costs }}</p>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p class="c-gray-700">{{ total_restrooms }}</p>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p class="fw-medium">${{ formatNumber(maintenance_labor_savings_total) }}</p>
          </div>
        </div>
        <div class="slo-iot-roi-calculator--section-row items-center py-md border-b-1 border-gray-300">
          <div class="px-md">
            <p class="mb-sm c-gray-700 fw-bold">{{ 'Line flush labor' | t }}</p>
            <ul class="m-0">
              <li class="c-gray-700">{{ 'Labor savings' | t }}</li>
              <li class="c-gray-700">{{ 'Minimize microbial growth' | t }}</li>
              <li class="c-gray-700">{{ 'Minimize health risks' | t }}</li>
              <li class="c-gray-700">{{ 'Lower maintenance labor cost' | t }}</li>
            </ul>
          </div>
          <div class="px-md">
            <SloField>
              <SloInput
                v-model.number="line_flush_labor_hours"
                name="line_flush_labor_hours"
                type="number"
                :disabled="!inputsEnabled"
              />
            </SloField>
          </div>
          <div class="px-md">
            <SloField>
              <SloSelect
                name="line_flush_labor_months"
                v-model.number="line_flush_labor_months"
                :disabled="!inputsEnabled"
              >
                <option value="" selected disabled></option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
              </SloSelect>
            </SloField>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p v-if="hourly_labor_costs" class="c-gray-700">${{ hourly_labor_costs }}</p>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p class="c-gray-700">{{ total_restrooms }}</p>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p class="fw-medium">${{ formatNumber(line_flush_labor_savings_total) }}</p>
          </div>
        </div>
        <div class="slo-iot-roi-calculator--section-row items-center py-md border-b-1 border-gray-300">
          <div class="px-md">
            <p class="mb-sm c-gray-700 fw-bold">{{ 'Report generation' | t }}</p>
            <ul class="m-0">
              <li class="c-gray-700">{{ 'Auto generated reports instead of consultant creating them' | t }}</li>
              <li class="c-gray-700">{{ 'Labor savings for manager creating reports (manager labor price used)' | t }}</li>
            </ul>
          </div>
          <div class="px-md">
            <SloField>
              <SloInput
                v-model.number="report_generation_labor_hours"
                name="report_generation_labor_hours"
                type="number"
                :disabled="!inputsEnabled"
              />
            </SloField>
          </div>
          <div class="px-md">
            <SloField>
              <SloSelect
                name="report_generation_labor"
                v-model.number="report_generation_labor_months"
                :disabled="!inputsEnabled"
              >
                <option value="" selected disabled></option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
              </SloSelect>
            </SloField>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p v-if="hourly_manager_labor_costs" class="c-gray-700">${{ hourly_manager_labor_costs }}</p>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p class="c-gray-700">{{ total_restrooms }}</p>
          </div>
          <div class="place-items-end basis-1 px-md">
            <p class="fw-medium">${{ formatNumber(report_generation_labor_savings_total) }}</p>
          </div>
        </div>
      </div>
      <div>
        <div class="bg-gray-50 border-b-1 border-gray-300">
          <div class="px-md py-sm">
            <h3 class="text-xl fw-strong m-0">{{ 'Compliance' | t }}</h3>
          </div>
          <div class="slo-iot-roi-calculator--section-row items-center py-md">
            <div class="px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Savings and value points' | t }}</h4>
            </div>
            <div class="place-items-end px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Labor hrs' | t }}</h4>
            </div>
            <div class="place-items-end px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Times per year' | t }}</h4>
            </div>
            <div class="place-items-end basis-0 px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Restrooms' | t }}</h4>
            </div>
            <div class="place-items-end basis-0 px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Savings' | t }}</h4>
            </div>
          </div>
        </div>
        <div class="slo-iot-roi-calculator--section-row items-center py-md border-b-1 border-gray-300">
          <div class="px-md">
            <p class="mb-sm c-gray-700 fw-bold">{{ 'Reclaimed water use for rebates, credits' | t }}</p>
            <ul class="m-0">
              <li class="c-gray-700">{{ 'Water savings documented for reclaimed water use' | t }}</li>
              <li class="c-gray-700">{{ 'Green / sustainability provides an NPV of $.51 per SF' | t }}</li>
            </ul>
          </div>
          <div class="px-md">
            <SloField>
              <SloInput
                v-model.number="reclaimed_labor_hours"
                name="reclaimed_labor_hours"
                type="number"
                :disabled="!inputsEnabled"
              />
            </SloField>
          </div>
          <div class="px-md">
            <SloField>
              <SloSelect
                name="reclaimed_labor_months"
                v-model.number="reclaimed_labor_months"
                :disabled="!inputsEnabled"
              >
                <option value="" selected disabled></option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
              </SloSelect>
            </SloField>
          </div>
          <div class="place-items-end basis-0 px-md">
            <p class="c-gray-700">{{ total_restrooms }}</p>
          </div>
          <div class="place-items-end basis-0 px-md">
            <p class="fw-medium">${{ formatNumber(reclaimed_labor_savings_total) }}</p>
          </div>
        </div>
        <div class="slo-iot-roi-calculator--section-row items-center py-md border-b-1 border-gray-300">
          <div class="px-md">
            <p class="mb-sm c-gray-700 fw-bold">{{ 'Water sub-metering' | t }}</p>
            <ul class="m-0">
              <li class="c-gray-700">{{ 'LEED points for sub-metering' | t }}</li>
              <li class="c-gray-700">{{ 'No consultant needed to determine submeter' | t }}</li>
              <li class="c-gray-700">{{ 'Patron uses less water once notified' | t }}</li>
              <li class="c-gray-700">{{ 'Lower water/sewer bill' | t }}</li>
            </ul>
          </div>
          <div class="px-md">
            <SloField>
              <SloInput
                v-model.number="water_sub_metering_hours"
                name="water_sub_metering_hours"
                type="number"
                :disabled="!inputsEnabled"
              />
            </SloField>
          </div>
          <div class="px-md">
            <SloField>
              <SloSelect
                name="water_sub_metering_months"
                v-model.number="water_sub_metering_months"
                :disabled="!inputsEnabled"
              >
                <option value="" selected disabled></option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
              </SloSelect>
            </SloField>
          </div>
          <div class="place-items-end basis-0 px-md">
            <p class="c-gray-700">{{ total_restrooms }}</p>
          </div>
          <div class="place-items-end basis-0 px-md">
            <p class="fw-medium">${{ formatNumber(water_sub_metering_savings_total) }}</p>
          </div>
        </div>
      </div>
      <div>
        <div class="bg-gray-50 border-b-1 border-gray-300">
          <div class="px-md py-sm">
            <h3 class="text-xl fw-strong m-0">{{ 'Parts' | t }}</h3>
          </div>
          <div class="slo-iot-roi-calculator--section-row py-md">
            <div class="px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Savings and value points' | t }}</h4>
            </div>
            <div class="place-items-end px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Part cost' | t }}</h4>
            </div>
            <div class="place-items-end px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Times per year' | t }}</h4>
            </div>
            <div class="place-items-end basis-0 px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Restrooms' | t }}</h4>
            </div>
            <div class="place-items-end basis-0 px-md">
              <h4 class="m-0 c-gray-600 text-sm fw-strong text-uppercase">{{ 'Savings' | t }}</h4>
            </div>
          </div>
        </div>
        <div class="slo-iot-roi-calculator--section-row items-center py-md border-b-1 border-gray-300">
          <div class="px-md">
            <p class="mb-sm c-gray-700 fw-bold">{{ 'Parts Costs' | t }}</p>
            <ul class="m-0">
              <li class="c-gray-700">{{ 'Replace only the parts that need replacing' | t }}</li>
            </ul>
          </div>
          <div class="px-md">
            <SloField>
              <SloInput
                v-model.number="part_costs"
                name="part_costs"
                type="number"
                :disabled="!inputsEnabled"
              >
                <template v-slot:start>
                  <SloIcon name="DollarSign" />
                </template>
            </SloInput>
            </SloField>
          </div>
          <div class="px-md">
            <SloField>
              <SloSelect
                v-model.number="part_months"
                name="part_months"
                :disabled="!inputsEnabled"
              >
                <option value="" selected disabled></option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
              </SloSelect>
            </SloField>
          </div>
          <div class="place-items-end basis-0 px-md">
            <p class="c-gray-700">{{ total_restrooms }}</p>
          </div>
          <div class="place-items-end basis-0 px-md">
            <p class="fw-medium">${{ formatNumber(part_savings_total) }}</p>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-end w-full p-lg">
        <div class="slo-iot-roi-calculator--savings">
          <p class="text-xl fw-strong">{{ 'Total' | t }}</p>
          <p class="text-xl fw-strong ml-md">${{ total }}</p>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import t from '../main/i18n';
import SloButton from '../../lib/components/SloButton.vue';
import SloField from '../../lib/components/SloField.vue';
import SloInput from '../../lib/components/SloInput.vue';
import SloLabel from '../../lib/components/SloLabel.vue';
import SloSelect from '../../lib/components/SloSelect.vue';
import SloIcon from '../../lib/components/SloIcon.vue';

export default {
  name: 'SloIOTROICalculator',
  components: {
    SloInput,
    SloField,
    SloLabel,
    SloSelect,
    SloButton,
    SloIcon
  },
  filters: {
    t
  },
  data() {
    return {
      hourly_labor_costs: null,
      hourly_manager_labor_costs: null,
      total_restrooms: null,
      estimated_number_of_products: null,
      maintenance_labor_hours: null,
      maintenance_labor_months: null,
      line_flush_labor_hours: null,
      line_flush_labor_months: null,
      report_generation_labor_hours: null,
      report_generation_labor_months: null,
      reclaimed_labor_hours: null,
      reclaimed_labor_months: null,
      water_sub_metering_hours: null,
      water_sub_metering_months: null,
      part_costs: null,
      part_months: null
    };
  },
  computed: {
    inputsEnabled() {
      return this.hourly_labor_costs && this.hourly_manager_labor_costs && this.total_restrooms && this.estimated_number_of_products;
    },
    maintenance_labor_savings_total() {
      return (
        (this.maintenance_labor_hours * this.hourly_labor_costs) *
        this.maintenance_labor_months *
        this.total_restrooms
      );
    },
    line_flush_labor_savings_total() {
      return (
        (this.line_flush_labor_hours * this.hourly_labor_costs) *
        this.line_flush_labor_months *
        this.total_restrooms
      );
    },
    report_generation_labor_savings_total() {
      return (
        this.report_generation_labor_hours *
        this.hourly_manager_labor_costs *
        this.report_generation_labor_months
      );
    },
    reclaimed_labor_savings_total() {
      return this.reclaimed_labor_hours * this.reclaimed_labor_months;
    },
    water_sub_metering_savings_total() {
      return this.water_sub_metering_hours * this.water_sub_metering_months;
    },
    part_savings_total() {
      return this.part_costs * this.part_months * this.total_restrooms;
    },
    total() {
      return this.formatNumber((
        this.maintenance_labor_savings_total +
        this.line_flush_labor_savings_total +
        this.report_generation_labor_savings_total +
        this.reclaimed_labor_savings_total +
        this.water_sub_metering_savings_total +
        this.part_savings_total
      ));
    }
  },
  methods: {
    formatNumber(number) {
      return number.toLocaleString('en-US', {minimumFractionDigits: 2});
    }
  }
};
</script>

<style lang="scss">
@import '../../scss/variables';
@import '../../scss/system';

.slo-iot-roi-calculator {
  max-width: 1280px;
  margin-inline: auto;
  overflow-x: scroll;

  @media (min-width: $screen-lg) {
    overflow-x: visible;
  }

  .slo-field {
    justify-content: space-between;
  }

  &--form-input-group {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 2.4rem;

    @media (min-width: $screen-sm) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (min-width: $screen-md) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  &--form-input-calculator {
    min-width: 992px;
  }

  &--section-row {
    display: flex;
    align-items: center;

    & > * {
      flex: 1 1 0px;
    }

    & > :first-child {
      flex: 0 1 360px;

      @media (min-width: $screen-lg) {
        flex: 0 1 570px;
      }
    }

    .basis-0 {
      flex: 0 1 150px;

      @media (min-width: $screen-lg) {
        flex: 1 1 0px;
      }
    }

    .basis-1 {
      flex: 0 1 100px;

      @media (min-width: $screen-lg) {
        flex: 0 1 119px;
      }
    }
  }

  &--savings {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    place-items: end;
    gap: 16px;

    @media (min-width: $screen-md) {
      gap: 32px;
    }
  }

  .place-items-end {
    display: flex;
    justify-content: flex-end;
  }

  .rounded-t-lg {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }

  .rounded-b-lg {
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  label, p {
    margin: 0;
  }
}
</style>
